import { Github, Terminal, Package, FolderTree, PenTool as Tool, Globe,  Code, Rocket, Settings, Layout, Palette,  FileJson, Server, Brush, Laptop } from 'lucide-react';


// Documentation sections data with subsections
export const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Rocket,
      subsections: [
        {
          id: 'unpacking',
          title: 'Unpacking the ZIP File',
          icon: Package,
          content: `
            1. Download the template ZIP file from your purchase email
            2. Right-click the ZIP file and select "Extract All" (Windows) or double-click (Mac)
            3. Choose a destination folder for the extracted contents
            4. Click "Extract" to unpack all files
            
            Note: Make sure you have enough disk space before extracting.
          `
        },
        {
          id: 'prerequisites',
          title: 'Prerequisites',
          icon: Settings,
          content: `
            Before you begin, ensure you have:
            
            • A modern web browser (Chrome, Firefox, Safari, or Edge)
            • Basic understanding of web development
            • Text editor or IDE installed
            • Terminal/Command Prompt access
            • Stable internet connection
          `
        }
      ]
    },
    {
      id: 'project-structure',
      title: 'Project Structure',
      icon: Layout,
      subsections: [
        {
          id: 'structure',
          title: 'Folder Structure',
          icon: FolderTree,
          content: `
            Your template contains the following structure:
            
            /src
            ├── components/     # Reusable UI components
            │   ├── common/     # Shared components
            │   ├── layout/     # Layout components
            │   └── features/   # Feature-specific components
            ├── pages/         # Page components
            ├── styles/        # CSS and styling files
            ├── utils/         # Utility functions
            ├── hooks/        # Custom React hooks
            ├── types/        # TypeScript type definitions
            └── App.tsx       # Main application file
            
            /public
            ├── assets/       # Static assets like images
            └── fonts/        # Custom font files
            
            /config/          # Configuration files
            package.json      # Dependencies and scripts
            README.md        # Basic documentation
          `
        },
        {
          id: 'components',
          title: 'Key Components',
          icon: Code,
          content: `
            Important components in the template:
            
            1. Layout Components
               • Header - Main navigation and branding
               • Footer - Site footer with links and info
               • Sidebar - Navigation sidebar component
            
            2. Feature Components
               • AuthForm - Authentication forms
               • Dashboard - Main dashboard layout
               • DataTable - Reusable table component
            
            3. Common Components
               • Button - Styled button component
               • Card - Container component
               • Modal - Popup dialog component
          `
        }
      ]
    },
    {
      id: 'development',
      title: 'Development',
      icon: Code,
      subsections: [
        {
          id: 'tools',
          title: 'Required Tools',
          icon: Tool,
          content: `
            Ensure you have the following installed:
            
            • Node.js (v18 or later)
            • Git
            • VS Code (recommended)
            • Terminal application
            
            Recommended VS Code extensions:
            • ESLint
            • Prettier
            • Tailwind CSS IntelliSense
            
            You can verify your Node.js installation by running:
            node --version
          `
        },
        {
          id: 'local',
          title: 'Local Development',
          icon: Terminal,
          content: `
            1. Open your terminal
            2. Navigate to the project folder:
               cd your-template-folder
            
            3. Install dependencies:
               npm install
            
            4. Start the development server:
               npm run dev
            
            Your site will be available at http://localhost:5173
            
            Additional commands:
            • npm run build - Build for production
            • npm run lint - Run ESLint
            • npm run test - Run tests
          `
        }
      ]
    },
    {
      id: 'customization',
      title: 'Customization',
      icon: Palette,
      subsections: [
        {
          id: 'styling',
          title: 'Styling Guide',
          icon: Brush,
          content: `
            The template uses Tailwind CSS for styling:
            
            1. Global Styles
               • Edit tailwind.config.js for theme customization
               • Modify src/styles/globals.css for custom CSS
            
            2. Component Styles
               • Use Tailwind classes directly in components
               • Create custom components for repeated styles
            
            3. Theme Customization
               • Colors - Update colors in tailwind.config.js
               • Typography - Modify font settings
               • Spacing - Adjust spacing scale
          `
        },
        {
          id: 'configuration',
          title: 'Configuration',
          icon: FileJson,
          content: `
            Important configuration files:
            
            1. vite.config.ts
               • Configure build settings
               • Add plugins
               • Set up aliases
            
            2. tsconfig.json
               • TypeScript configuration
               • Path aliases
               • Compiler options
            
            3. .env files
               • Environment variables
               • API keys
               • Configuration values
          `
        }
      ]
    },
    {
      id: 'deployment',
      title: 'Deployment',
      icon: Globe,
      subsections: [
        {
          id: 'github',
          title: 'GitHub Setup',
          icon: Github,
          content: `
            1. Create a new repository on GitHub
            
            2. Initialize your local repository:
               git init
               git add .
               git commit -m "Initial commit"
            
            3. Add your GitHub repository as remote:
               git remote add origin your-repo-url
            
            4. Push your code:
               git push -u origin main
            
            Best practices:
            • Use meaningful commit messages
            • Create a .gitignore file
            • Set up branch protection rules
          `
        },
        {
          id: 'cloudflare',
          title: 'Cloudflare Pages',
          icon: Server,
          content: `
            1. Log in to your Cloudflare dashboard
            
            2. Navigate to Pages > Create a project
            
            3. Connect your GitHub repository
            
            4. Configure your build settings:
               • Build command: npm run build
               • Build output directory: dist
               • Node.js version: 18
            
            5. Environment variables:
               • Add necessary environment variables
               • Set up preview branches
            
            6. Click "Save and Deploy"
            
            Your site will be live in a few minutes!
            
            Additional deployment options:
            • Custom domains
            • SSL/TLS configuration
            • Cache settings
          `
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: Settings,
      subsections: [
        {
          id: 'common-issues',
          title: 'Common Issues',
          icon: Terminal,
          content: `
            Common problems and solutions:
  
            1. Node.js version mismatch
               • Error: "The engine "node" is incompatible..."
               • Solution: Update Node.js to v18 or later
  
            2. Missing dependencies
               • Error: "Cannot find module..."
               • Solution: Run npm install
  
            3. Build errors
               • Error: "Build failed with exit code 1"
               • Solution: Check console for specific error messages
               • Verify all required environment variables are set
          `
        },
        {
          id: 'performance',
          title: 'Performance Tips',
          icon: Laptop,
          content: `
            Optimize your application:
  
            1. Code Splitting
               • Use React.lazy() for route-based code splitting
               • Import heavy components dynamically
  
            2. Image Optimization
               • Use appropriate image formats
               • Implement lazy loading
               • Optimize image sizes
  
            3. Caching Strategy
               • Implement service workers
               • Use proper cache headers
               • Optimize API responses
          `
        }
      ]
    }
  ];